import React, { useState, useEffect } from 'react';
import {
  Card, Row, Col, Table, Button, Modal,
  Tag, Badge, Statistic, Select, Space,
  Descriptions, message, Timeline, Spin
} from 'antd';
import {
  EyeOutlined, CheckCircleOutlined,
  CloseCircleOutlined, ReloadOutlined,
} from '@ant-design/icons';
import {
  collection, getDocs, doc, updateDoc,
  query, orderBy, serverTimestamp, addDoc
} from 'firebase/firestore';
import { db } from '../../services/firebase';
import { CARD_STYLE, GOLD_GRADIENT } from '../../constants/theme';

const { Option } = Select;

const STATUS_CONFIG = {
  placed: { color: '#1890FF', badge: 'processing', label: 'Placed', step: 0 },
  confirmed: { color: '#C9A84C', badge: 'warning', label: 'Confirmed', step: 1 },
  packed: { color: '#722ED1', badge: 'processing', label: 'Packed', step: 2 },
  dispatched: { color: '#FAAD14', badge: 'warning', label: 'Dispatched', step: 3 },
  delivered: { color: '#52C41A', badge: 'success', label: 'Delivered', step: 4 },
  cancelled: { color: '#FF4D4F', badge: 'error', label: 'Cancelled', step: -1 },
  returned: { color: '#FF7A45', badge: 'error', label: 'Returned', step: -1 },
};

const TIER_COLORS = {
  economy: '#52C41A', premium: '#1890FF',
  luxury: '#C9A84C', super_luxury: '#722ED1',
};

export default function OrderManagement() {
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [viewModal, setViewModal] = useState(false);
  const [actionModal, setActionModal] = useState(false);
  const [actionType, setActionType] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => { loadOrders(); }, []);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, 'orders'), orderBy('placedAt', 'desc'));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data(),
        placedAt: d.data().placedAt?.toDate?.()?.toLocaleString('en-IN') || 'N/A',
        deliveredAt: d.data().deliveredAt?.toDate?.()?.toLocaleString('en-IN') || null,
      }));
      setOrders(data);
    } catch (error) {
      message.error('Failed to load orders');
    }
    setLoading(false);
  };

  const stats = {
    total: orders.length,
    active: orders.filter(o => !['delivered', 'cancelled', 'returned'].includes(o.status)).length,
    delivered: orders.filter(o => o.status === 'delivered').length,
    cancelled: orders.filter(o => o.status === 'cancelled').length,
  };

  const filtered = filterStatus === 'all' ? orders : orders.filter(o => o.status === filterStatus);

  const handleAction = async (type) => {
    setActionLoading(true);
    try {
      const newStatus = type === 'confirm' ? 'confirmed' : type === 'cancel' ? 'cancelled' : 'dispatched';
      await updateDoc(doc(db, 'orders', selected.id), {
        status: newStatus,
        updatedAt: serverTimestamp(),
      });
      await addDoc(collection(db, 'auditLogs'), {
        action: 'ORDER ' + newStatus.toUpperCase(),
        details: 'Order: ' + selected.orderId + ' | Customer: ' + selected.customerName + ' | Amount: Rs.' + selected.totalAmount,
        adminEmail: 'nksapcolouracademy@gmail.com',
        targetId: selected.id, targetType: 'order',
        timestamp: serverTimestamp(),
      });
      setOrders(prev => prev.map(o => o.id === selected.id ? { ...o, status: newStatus } : o));
      message.success('Order status updated to ' + newStatus + ' — saved to Firebase');
      setActionModal(false); setViewModal(false);
    } catch (error) {
      message.error('Action failed');
    }
    setActionLoading(false);
  };

  const columns = [
    {
      title: 'Order ID',
      dataIndex: 'orderId',
      render: (id) => <span style={{ color: '#C9A84C', fontFamily: 'monospace', fontWeight: 500 }}>{id}</span>,
    },
    {
      title: 'Customer',
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 500, color: '#D4DCE3' }}>{r.customerName}</div>
          <div style={{ fontSize: 11, color: '#7A8A96' }}>{r.customerCity}</div>
        </div>
      ),
    },
    {
      title: 'Product',
      render: (_, r) => (
        <div>
          <div style={{ color: '#D4DCE3', fontSize: 12 }}>{r.productName}</div>
          <div style={{ fontSize: 11, color: '#7A8A96' }}>{r.dealerName}</div>
        </div>
      ),
    },
    {
      title: 'Amount',
      render: (_, r) => (
        <div>
          <div style={{ color: '#C9A84C', fontWeight: 500 }}>Rs.{r.totalAmount?.toLocaleString()}</div>
          <div style={{ fontSize: 11, color: '#7A8A96' }}>{r.quantity} {r.unit}</div>
        </div>
      ),
    },
    {
      title: 'Tier',
      dataIndex: 'tier',
      render: (tier) => tier ? (
        <Tag style={{ background: (TIER_COLORS[tier] || '#7A8A96') + '20', color: TIER_COLORS[tier] || '#7A8A96', border: 'none', borderRadius: 20, fontSize: 11, textTransform: 'capitalize' }}>
          {tier}
        </Tag>
      ) : null,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (status) => {
        const s = STATUS_CONFIG[status] || STATUS_CONFIG.placed;
        return <Badge status={s.badge} text={<span style={{ color: s.color, fontSize: 12 }}>{s.label}</span>} />;
      },
    },
    {
      title: 'Placed',
      dataIndex: 'placedAt',
      render: (t) => <span style={{ color: '#7A8A96', fontSize: 11 }}>{t}</span>,
    },
    {
      title: 'Actions',
      render: (_, r) => (
        <Space>
          <Button type="text" icon={<EyeOutlined />} style={{ color: '#C9A84C' }}
            onClick={() => { setSelected(r); setViewModal(true); }} />
          {r.status === 'placed' && (
            <Button type="text" size="small" style={{ color: '#C9A84C', borderColor: 'rgba(201,168,76,0.3)', fontSize: 11 }}
              onClick={() => { setSelected(r); setActionType('confirm'); setActionModal(true); }}>
              Confirm
            </Button>
          )}
          {['placed', 'confirmed'].includes(r.status) && (
            <Button type="text" size="small" danger
              onClick={() => { setSelected(r); setActionType('cancel'); setActionModal(true); }}>
              Cancel
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {[
          { title: 'Total Orders', value: stats.total, color: '#C9A84C' },
          { title: 'Active Orders', value: stats.active, color: '#1890FF' },
          { title: 'Delivered', value: stats.delivered, color: '#52C41A' },
          { title: 'Cancelled', value: stats.cancelled, color: '#FF4D4F' },
        ].map((stat, i) => (
          <Col xs={12} lg={6} key={i}>
            <Card style={CARD_STYLE} bodyStyle={{ padding: 20 }}>
              <Statistic title={<span style={{ color: '#7A8A96', fontSize: 12 }}>{stat.title}</span>}
                value={stat.value} valueStyle={{ color: stat.color, fontSize: 28, fontWeight: 600 }} />
            </Card>
          </Col>
        ))}
      </Row>

      <Card style={{ ...CARD_STYLE, marginBottom: 16 }} bodyStyle={{ padding: '12px 16px' }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ color: '#7A8A96', fontSize: 13 }}>Filter:</span>
          <Select value={filterStatus} onChange={setFilterStatus} style={{ width: 160 }}>
            <Option value="all">All Orders</Option>
            <Option value="placed">Placed</Option>
            <Option value="confirmed">Confirmed</Option>
            <Option value="packed">Packed</Option>
            <Option value="dispatched">Dispatched</Option>
            <Option value="delivered">Delivered</Option>
            <Option value="cancelled">Cancelled</Option>
          </Select>
          <Button type="text" icon={<ReloadOutlined />} style={{ color: '#C9A84C' }} onClick={loadOrders}>Refresh</Button>
          <span style={{ marginLeft: 'auto', color: '#7A8A96', fontSize: 12 }}>
            {loading ? 'Loading...' : `${filtered.length} orders`}
          </span>
        </div>
      </Card>

      <Card style={CARD_STYLE} bodyStyle={{ padding: 0 }}>
        <Spin spinning={loading}>
          <Table dataSource={filtered} columns={columns} rowKey="id" pagination={{ pageSize: 10 }} />
        </Spin>
      </Card>

      <Modal title={<span style={{ color: '#C9A84C' }}>Order Details - {selected?.orderId}</span>}
        open={viewModal} onCancel={() => setViewModal(false)} width={680}
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button onClick={() => setViewModal(false)}>Close</Button>
            {selected?.status === 'placed' && (
              <>
                <Button danger onClick={() => { setActionType('cancel'); setActionModal(true); }}>Cancel Order</Button>
                <Button style={{ background: GOLD_GRADIENT, border: 'none', color: '#0D0D0D', fontWeight: 600 }}
                  onClick={() => { setActionType('confirm'); setActionModal(true); }}>Force Confirm</Button>
              </>
            )}
          </div>
        }>
        {selected && (
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="Order ID">
              <span style={{ color: '#C9A84C', fontFamily: 'monospace' }}>{selected.orderId}</span>
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Badge status={STATUS_CONFIG[selected.status]?.badge}
                text={<span style={{ color: STATUS_CONFIG[selected.status]?.color }}>{STATUS_CONFIG[selected.status]?.label}</span>} />
            </Descriptions.Item>
            <Descriptions.Item label="Customer">{selected.customerName}</Descriptions.Item>
            <Descriptions.Item label="Phone">{selected.customerPhone}</Descriptions.Item>
            <Descriptions.Item label="Product" span={2}>{selected.productName}</Descriptions.Item>
            <Descriptions.Item label="Dealer">{selected.dealerName}</Descriptions.Item>
            <Descriptions.Item label="Quantity">{selected.quantity} {selected.unit}</Descriptions.Item>
            <Descriptions.Item label="Order Value">Rs.{selected.orderValue?.toLocaleString()}</Descriptions.Item>
            <Descriptions.Item label="Delivery Charge">Rs.{selected.deliveryCharge}</Descriptions.Item>
            <Descriptions.Item label="Total Amount">
              <span style={{ color: '#C9A84C', fontWeight: 600 }}>Rs.{selected.totalAmount?.toLocaleString()}</span>
            </Descriptions.Item>
            <Descriptions.Item label="Payment">{selected.paymentMethod} — {selected.paymentStatus}</Descriptions.Item>
            <Descriptions.Item label="Placed At">{selected.placedAt}</Descriptions.Item>
            <Descriptions.Item label="Delivery Address" span={2}>
              {selected.deliveryAddress?.line1}, {selected.deliveryAddress?.city} - {selected.deliveryAddress?.pincode}
            </Descriptions.Item>
            {selected.deliveryAgentName && (
              <Descriptions.Item label="Delivery Agent" span={2}>
                {selected.deliveryAgentName} • {selected.deliveryAgentPhone}
              </Descriptions.Item>
            )}
            {selected.cancelReason && (
              <Descriptions.Item label="Cancel Reason" span={2}>
                <span style={{ color: '#FF4D4F' }}>{selected.cancelReason}</span>
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>

      <Modal
        title={<span style={{ color: actionType === 'cancel' ? '#FF4D4F' : '#C9A84C' }}>
          {actionType === 'cancel' ? 'Cancel Order' : 'Confirm Order'} - {selected?.orderId}
        </span>}
        open={actionModal} onCancel={() => setActionModal(false)}
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button onClick={() => setActionModal(false)}>Cancel</Button>
            <Button loading={actionLoading} danger={actionType === 'cancel'}
              style={actionType !== 'cancel' ? { background: GOLD_GRADIENT, border: 'none', color: '#0D0D0D', fontWeight: 600 } : {}}
              onClick={() => handleAction(actionType)}>
              {actionType === 'cancel' ? 'Confirm Cancellation' : 'Confirm Order'}
            </Button>
          </div>
        }>
        <div style={{ padding: '16px 0', color: '#B0B8C1', fontSize: 13 }}>
          {actionType === 'cancel'
            ? 'This will cancel the order and initiate a refund. Change saved to Firebase.'
            : 'This will force-confirm the order. Change saved to Firebase.'}
        </div>
        <div style={{ padding: '12px 16px', background: 'rgba(201,168,76,0.08)', border: '0.5px solid rgba(201,168,76,0.2)', borderRadius: 8 }}>
          <div style={{ color: '#D4DCE3', fontWeight: 500 }}>{selected?.productName}</div>
          <div style={{ color: '#7A8A96', fontSize: 12, marginTop: 4 }}>
            {selected?.customerName} • Rs.{selected?.totalAmount?.toLocaleString()}
          </div>
        </div>
      </Modal>
    </div>
  );
}